import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Banner, Button, Card, TextField } from "./components.js";
import { ApiError } from "./api.js";
import { describeSignInError } from "./password-session.js";
import { useSession } from "./session.js";
import { useTheme } from "./shell.js";
export const MIN_PASSWORD_LENGTH = 10;
function usePasswordAuth() {
    const s = useSession();
    if (!s.password)
        throw new Error("This page needs PasswordSessionProvider");
    return { password: s.password, signOut: s.signOut };
}
/** Email and password. The password is cleared as soon as the form is sent, whatever the answer. */
export function PasswordSignInPage({ title, onUseToken }) {
    const { password: auth } = usePasswordAuth();
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useTheme();
    async function submit(e) {
        e.preventDefault();
        if (busy)
            return;
        const sent = pw;
        setPw("");
        setError(null);
        setBusy(true);
        try {
            await auth.login(email.trim(), sent);
        }
        catch (err) {
            setError(describeSignInError(err));
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "g-signin", children: _jsx(Card, { title: title, children: _jsxs("form", { className: "g-stack", onSubmit: submit, noValidate: false, children: [auth.notice && _jsx(Banner, { tone: "success", title: "Done", children: auth.notice }), error && _jsx(Banner, { tone: "danger", title: "Could not sign in", children: error }), _jsx(TextField, { label: "Email", type: "email", name: "email", autoComplete: "username", value: email, onChange: (e) => { setEmail(e.target.value); setError(null); }, required: true, spellCheck: false, autoCapitalize: "none" }), _jsx(TextField, { label: "Password", type: show ? "text" : "password", name: "password", autoComplete: "current-password", value: pw, onChange: (e) => { setPw(e.target.value); setError(null); }, required: true }), _jsxs("label", { className: "g-check", children: [_jsx("input", { type: "checkbox", checked: show, onChange: (e) => setShow(e.target.checked) }), " Show password"] }), _jsx(Button, { type: "submit", variant: "primary", loading: busy, disabled: email.trim() === "" || pw === "", children: "Sign in" }), onUseToken && _jsx("button", { type: "button", className: "g-link", onClick: onUseToken, children: "Use a development token instead" })] }) }) }));
}
/** Shown, and nothing else, while a one-time password is in use. */
export function ChangePasswordPage({ title }) {
    const { password: auth, signOut } = usePasswordAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [again, setAgain] = useState("");
    const [busy, setBusy] = useState(false);
    const [problems, setProblems] = useState([]);
    useTheme();
    async function submit(e) {
        e.preventDefault();
        if (busy)
            return;
        const local = [];
        if (next.length < MIN_PASSWORD_LENGTH)
            local.push(`The new password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
        if (next !== again)
            local.push("The two new passwords are not the same.");
        if (local.length) {
            setProblems(local);
            return;
        }
        const [c, n] = [current, next];
        setCurrent("");
        setNext("");
        setAgain("");
        setProblems([]);
        setBusy(true);
        try {
            await auth.changePassword(c, n);
        }
        catch (err) {
            if (err instanceof ApiError && err.code === "invalid_credentials")
                setProblems(["The current password is not right."]);
            else if (err instanceof ApiError && err.details.length > 0)
                setProblems(err.details.map((d) => d.reason.charAt(0).toUpperCase() + d.reason.slice(1) + "."));
            else if (err instanceof ApiError && err.category === "RateLimited")
                setProblems(["Too many attempts. Wait a few minutes and try again."]);
            else
                setProblems(["Could not change the password. Try again."]);
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "g-signin", children: _jsx(Card, { title: title, children: _jsxs("form", { className: "g-stack", onSubmit: submit, children: [_jsxs(Banner, { tone: "warn", title: "Choose your own password", children: ["You signed in with a one-time password. Choose a password only you know before you continue", auth.email ? ` (${auth.email})` : "", "."] }), problems.length > 0 && _jsx(Banner, { tone: "danger", title: "Check the form", children: _jsx("ul", { className: "g-detail-list", children: problems.map((p) => _jsx("li", { children: p }, p)) }) }), _jsx(TextField, { label: "Current (one-time) password", type: "password", name: "current-password", autoComplete: "current-password", value: current, onChange: (e) => setCurrent(e.target.value), required: true }), _jsx(TextField, { label: "New password", type: "password", name: "new-password", autoComplete: "new-password", value: next, onChange: (e) => setNext(e.target.value), required: true, hint: `At least ${MIN_PASSWORD_LENGTH} characters. A short passphrase of a few words is fine.` }), _jsx(TextField, { label: "New password again", type: "password", name: "new-password-again", autoComplete: "new-password", value: again, onChange: (e) => setAgain(e.target.value), required: true }), _jsxs("div", { className: "g-row", children: [_jsx(Button, { type: "submit", variant: "primary", loading: busy, disabled: current === "" || next === "" || again === "", children: "Change password" }), _jsx(Button, { onClick: signOut, children: "Sign out" })] })] }) }) }));
}
